import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Col, Row, Container, Modal } from "react-bootstrap";
import {
  getActiveListings,
  getAllAttributes,
  getAllBoatTypes,
  getUserByID,
  getAllBookings,
  getAllReviews,
} from "../utils";
import {
  calculateBoatPerformanceScore,
} from "../rankingUtils";
import UserAvatar from "../UserAvatar";
import "./SearchBarStyle.css";
import DateRangeSelect from "./DateRangeSelect";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { addDays } from "date-fns";
import GoogleMaps from "../GoogleMaps";
import { MultiSelect } from "react-multi-select-component";
import { Skeleton } from "@mui/material";
import BoatTypeSelect from "./BoatTypeSelect";
import PassengerSelect from "./PassengerSelect";

import { CiCalendarDate } from "react-icons/ci";
import { CiGlobe } from "react-icons/ci";
import { PiBoatLight } from "react-icons/pi";
import { BsListStars, BsPeople } from "react-icons/bs";
import { GrClose, GrPowerReset } from "react-icons/gr";
import { FaStar } from "react-icons/fa";
import { RiShip2Fill } from "react-icons/ri";
import { GiFishing, GiBoatFishing } from "react-icons/gi";
import { faAnchor, faShip, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { faGauge } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from "../../contexts/AuthContext";
import { useSearchFilters } from "../../contexts/SearchFilterContext";
import "./FloatingButtons.css";
import { getDatabase, ref, update } from "firebase/database";

const EXCLUDED_BOOKING_STATUSES = new Set([
  "cancelled",
  "pending",
  "trip concluded",
  "trip completed",
  "trip returned",
]);

function reviewsForListing(allReviewsObject, listingId) {
  const revObj = allReviewsObject?.[listingId];
  if (!revObj) return [];
  return Object.keys(revObj).map((key) => ({ id: key, ...revObj[key] }));
}

function indexBookingsByListing(allBookings) {
  const map = {};
  (allBookings || []).forEach((booking) => {
    if (!booking?.listingId) return;
    if (!map[booking.listingId]) map[booking.listingId] = [];
    map[booking.listingId].push(booking);
  });
  return map;
}

function buildListingEvents(listing, bookingsByListingId) {
  const listingBookings = bookingsByListingId[listing.id] || [];
  const resolvedEvents = listingBookings
    .filter((booking) => {
      if (booking?.isDeleted === true) return false;
      const status = booking?.status?.toLowerCase();
      if (EXCLUDED_BOOKING_STATUSES.has(status)) return false;
      return booking.bookedDates && booking.bookedDates.length > 0;
    })
    .map((booking) => ({
      id: booking.id,
      start: new Date(booking.bookedDates[0]),
      end: new Date(booking.bookedDates[booking.bookedDates.length - 1]),
    }));

  return [...resolvedEvents, ...lockDateEvents(listing)];
}

function computeCombinedScore(reviews) {
  if (!reviews?.length) return 0;

  let totalRating = 0;
  let ratingCount = 0;

  reviews.forEach((review) => {
    const categoryRatings = [
      review["Listing Accuracy"],
      review["Itinerary & Experience"],
      review["Vessel & Equipment"],
      review["Departure & Return"],
      review["Value"],
      review["Communication"],
    ].filter((r) => r !== undefined && r !== null);

    if (categoryRatings.length > 0) {
      totalRating += categoryRatings.reduce((sum, r) => sum + r, 0) / categoryRatings.length;
      ratingCount++;
    }
  });

  const avgRating = ratingCount > 0 ? totalRating / ratingCount : 0;
  const reviewCountFactor = Math.min(reviews.length / 4, 5);
  return avgRating * 0.7 + reviewCountFactor * 0.3;
}

function toBoatLocation(listing) {
  const lat = listing?.listingAddress?.lat ?? listing?.storageAddress?.lat;
  const lng = listing?.listingAddress?.lng ?? listing?.storageAddress?.lng;
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;

  return {
    name: listing?.listingTitle,
    lat: parseFloat(lat),
    lng: parseFloat(lng),
    link: "listing/" + listing?.id,
    boatType: listing?.boatType,
    icon: `${process.env.REACT_APP_BASE_URL}/static/media/loading.33ed2d822776ba990a07.png`,
  };
}

function sortListings(listingsWithScores) {
  return [...listingsWithScores].sort((a, b) => {
    if (Math.abs(a.combinedScore - b.combinedScore) > 0.1) {
      return b.combinedScore - a.combinedScore;
    }
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return (b.performanceScore || 0) - (a.performanceScore || 0);
  });
}

function lockDateEvents(listing) {
  if (!listing.lockDates) return [];
  return Object.values(listing.lockDates).map((date) => ({
    id: `lockDate_${date}`,
    start: new Date(date),
    end: new Date(date),
    type: "lockDate",
  }));
}

function toQuickListing(listing) {
  const lat = listing?.storageAddress?.lat;
  const lng = listing?.storageAddress?.lng;
  return {
    ...listing,
    listingEvents: lockDateEvents(listing),
    features: listing?.features || [],
    listingAddress: { lat, lng },
    listingGeoData: listing.cachedGeoData || [],
    performanceScore: 0,
    reviews: 0,
    combinedScore: 0,
  };
}

function enrichListing(listing, listingReviewsMap, ownerDetailsMap, bookingsByListingId, allBookings) {
  const reviews = listingReviewsMap[listing.id] || [];
  const owner = ownerDetailsMap[listing.id] || {};
  const lat = listing?.storageAddress?.lat;
  const lng = listing?.storageAddress?.lng;

  return {
    ...listing,
    listingEvents: buildListingEvents(listing, bookingsByListingId),
    features: listing?.features || [],
    listingAddress: { lat, lng },
    listingGeoData: listing.cachedGeoData || [],
    performanceScore: calculateBoatPerformanceScore(
      listing,
      reviews,
      allBookings || [],
      owner,
      {}
    ),
    reviews: reviews.length,
    combinedScore: computeCombinedScore(reviews),
  };
}

async function fetchOwnersById(ownerIds) {
  const uniqueIds = [...new Set((ownerIds || []).filter(Boolean))];
  const entries = await Promise.all(
    uniqueIds.map(async (ownerId) => {
      try {
        const owner = await getUserByID(ownerId);
        return [ownerId, owner || {}];
      } catch (error) {
        console.warn(`Error fetching owner ${ownerId}:`, error);
        return [ownerId, {}];
      }
    })
  );
  return Object.fromEntries(entries);
}

// Survives Boats remounting when the user opens a listing and comes back
let homePhase1Promise = null;

function getHomePhase1Promise(startFetch) {
  if (!homePhase1Promise) {
    homePhase1Promise = Promise.resolve()
      .then(startFetch)
      .finally(() => {
        homePhase1Promise = null;
      });
  }
  return homePhase1Promise;
}

export default function Boats() {
  const { currentUser } = useAuth();
  const {
    listings,
    setListings,
    boatLocations,
    setBoatLocations,
    filtersOptions,
    setFilterOptions,
    boatTypeOptions,
    setBoatTypeOptions,
    boatTypesFilters,
    setBoatTypesFilters,
    locationNames,
    setLocationNames,
    boatFilters,
    setBoatFilters,
    ownerDetails, setOwnerDetails,
    listingReviews, setListingReviews,
    homeListingsReady, setHomeListingsReady,
    // Filter selection state (persisted in context)
    selectedOptions, setSelectedOptions,
    selectedBoatTypeOptions, setSelectedBoatTypeOptions,
    selectedFiltersOptions, setSelectedFiltersOptions,
    grabSelectedFilterOptions, setGrabSelectedFilterOptions,
    grabSearchLocation, setGrabSearchLocation,
    searchAddress, setSearchAddress,
    searchedLocation, setSearchedLocation,
    searchedLocationGeoData, setSearchedLocationGeoData,
    selectedDateRange, setSelectedDateRange,
    dateRange, setDateRange,
    appliedAdultsCount, setAppliedAdultsCount,
    appliedChildrenCount, setAppliedChildrenCount,
    adultsCount, setAdultsCount,
    childrenCount, setChildrenCount,
    currentPage, setCurrentPage,
  } = useSearchFilters();

  // For debugging performance scores - toggle this to show/hide scores
  const [showPerformanceScores, setShowPerformanceScores] = useState(false);

  console.log("filtersOptionsfiltersOptions", filtersOptions);

  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [selectedBoatTypes, setSelectedBoatTypes] = useState([]);
  const boatTypesRef = useRef([]);

  const [listingsPerPage] = useState(9);
  const [handleToggleTypesDropdown, setHandleToggleTypesDropdown] =
    useState(false);

  const [availableBoatsByBoatType, setAvailableBoatsByBoatType] = useState(0);
  const [availableBoatsByBoatFeatures, setAvailableBoatsByBoatFeatures] =
    useState(0);

  const boatFiltersRef = useRef([]);

  const [handleToggleFeaturesDropdown, setHandleToggleFeaturesDropdown] =
    useState(false);

  // Passengers filter state
  const [handleTogglePassengersDropdown, setHandleTogglePassengersDropdown] =
    useState(false);
  const [availableBoatsByCapacity, setAvailableBoatsByCapacity] = useState(0);

  const [isBoatsLoading, setIsBoatsLoading] = useState(() => !homeListingsReady);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [showFishModal, setShowFishModal] = useState(false);
  const [showBassBoatModal, setShowBassBoatModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const goToOwnerBoats = (event, ownerId) => {
    if (!ownerId) return;
    event.preventDefault();
    event.stopPropagation();
    navigate(`/boat-owner/${ownerId}`);
  };

  const handleDateRangeChange = (value) => {
    if (value == null) {
      setSelectedDateRange([]);
    } else {
      setSelectedDateRange(value);
    }
  };

  const handleFilterByDateRange = () => {
    setDateRange(selectedDateRange);
  };

  const today = new Date();
  const tomorrow = addDays(new Date(), 1);
  const yesterday = addDays(new Date(), -1);
  const calculateDistance = (locationA, locationB) => {
    if (!locationA || !locationB) return null;

    const lat1 = locationA?.lat;
    const lon1 = locationA?.lng;
    const lat2 = locationB?.lat;
    const lon2 = locationB?.lng;

    // Check if any coordinates are missing
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;

    // Calculate distance using Haversine formula
    const R = 6371 * 1000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  };

  const ADDRESS_TYPE_PRIORITY = [
    "locality",
    "administrative_area_level_2",
    "administrative_area_level_1",
    "country",
  ];

  const getBestAddressType = (types) => {
    if (!types || types.length === 0) return null;
    for (const priorityType of ADDRESS_TYPE_PRIORITY) {
      if (types.includes(priorityType)) return priorityType;
    }
    return types[0];
  };

  const hasValidCoordinates = (lat, lng) => {
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return false;
    if (lat === 0 && lng === 0) return false;
    return true;
  };

  const matchesLocationFilter = (listing, searchedLocationGeo) => {
    const listingGeoData = listing.listingGeoData;
    const searchedTypes = searchedLocationGeo?.[0]?.types || [];
    const addressType = getBestAddressType(searchedTypes);

    if (!addressType || !listingGeoData?.length || !searchedLocationGeo?.[0]) {
      return false;
    }

    const listingComponent = listingGeoData[0]?.address_components?.find(
      (component) => component?.types?.includes(addressType)
    );
    const searchedComponent = searchedLocationGeo[0]?.address_components?.find(
      (component) => component?.types?.includes(addressType)
    );

    if (!listingComponent || !searchedComponent) {
      return false;
    }

    return listingComponent.long_name === searchedComponent.long_name;
  };

  const matchesBoatTypeFilter = (listing, boatTypeOptions) => {
    if (!boatTypeOptions || boatTypeOptions.length === 0) return true;

    return boatTypeOptions.some((option) => {
      if (option.value === "Fishing Guide" && listing.boatType === "Fishing Charter") {
        return true;
      }
      if (option.value === "Fishing Charter" && listing.boatType === "Fishing Guide") {
        return true;
      }
      return option.value === listing.boatType;
    });
  };

  const enrichHomeListings = async (availableBoats) => {
    const ownersPromise = fetchOwnersById(
      availableBoats.map((listing) => listing.ownerId)
    );
    const enrichmentPromise = Promise.all([getAllReviews(), getAllBookings()]);

    const ownersByUserId = await ownersPromise;
    const ownerDetailsMap = {};
    availableBoats.forEach((listing) => {
      ownerDetailsMap[listing.id] = ownersByUserId[listing.ownerId] || {};
    });
    setOwnerDetails(ownerDetailsMap);

    const [allReviewsObject, allBookings] = await enrichmentPromise;
    const listingReviewsMap = {};
    availableBoats.forEach((listing) => {
      listingReviewsMap[listing.id] = reviewsForListing(allReviewsObject, listing.id);
    });

    const bookingsByListingId = indexBookingsByListing(allBookings);
    const enrichedListings = availableBoats.map((listing) =>
      enrichListing(
        listing,
        listingReviewsMap,
        ownerDetailsMap,
        bookingsByListingId,
        allBookings
      )
    );

    setListingReviews(listingReviewsMap);
    setListings(sortListings(enrichedListings));
    setBoatLocations(enrichedListings.map(toBoatLocation).filter(Boolean));
  };

  const runHomeListingsFetch = async () => {
    const [activeListings, allAttributes, allBoatTypes] = await Promise.all([
      getActiveListings(),
      getAllAttributes(),
      getAllBoatTypes(),
    ]);

    const availableBoats = (activeListings || []).filter((b) => b?.boatListed);
    const optionsArray = Object.values(allAttributes[2] || {});
    const quickListings = availableBoats.map(toQuickListing);
    const locationNamesMap = {};
    quickListings.forEach((listing) => {
      locationNamesMap[listing.id] = listing.storageAddressText || "Location not available";
    });

    const nextBoatTypeOptions = (allBoatTypes || []).map((boatType) => ({
      label: boatType,
      value: boatType,
    }));
    const newFiltersOptions = (optionsArray || []).map((option) => ({
      label: option,
      value: option,
    }));
    const filteredBoatTypes = nextBoatTypeOptions.sort((a, b) => {
      if (a.label === "Other") return 1;
      if (b.label === "Other") return -1;
      return a.label.localeCompare(b.label);
    });

    setListings(quickListings);
    setBoatLocations(quickListings.map(toBoatLocation).filter(Boolean));
    setLocationNames(locationNamesMap);
    setFilterOptions([...newFiltersOptions]);
    boatTypesRef.current = [...filteredBoatTypes];
    boatFiltersRef.current = [...newFiltersOptions];
    setBoatTypeOptions([...filteredBoatTypes]);
    setBoatTypesFilters(allBoatTypes);
    setBoatFilters(optionsArray);
    setHomeListingsReady(true);

    enrichHomeListings(availableBoats).catch((enrichError) => {
      console.error("Error enriching listings:", enrichError.message || enrichError);
    });
  };

  const fetchListings = async () => {
    if (homeListingsReady) {
      return;
    }

    setIsBoatsLoading(true);
    try {
      await getHomePhase1Promise(runHomeListingsFetch);
    } catch (error) {
      console.error("Error fetching listings:", error.message || error);
      setListings((prev) => (prev?.length ? prev : []));
      setBoatLocations((prev) => (prev?.length ? prev : []));
    } finally {
      setIsBoatsLoading(false);
    }
  };

  useEffect(() => {
    if (homeListingsReady) {
      return;
    }
    fetchListings();
  }, []);



  function isDateRangeOverlap(listingEvents, selectedStart, selectedEnd) {
    if (!listingEvents) return;

    for (let i = 0; i < listingEvents.length; i++) {
      const event = listingEvents[i];

      // Convert start and end strings to Date objects
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      // Check if event falls within the selected range
      if (eventStart <= selectedEnd && eventEnd >= selectedStart) {
        return true; // Return true if there's an overlap
      }
    }
    return false; // Return false if no overlap found
  }

  const getLocationDetails = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
      );
      const data = await response.json();
      if (data.status === "OK") {
        const address = data.results[0];
        return address;
      } else {
        return "Reverse geocoding failed:", data.status;
      }
    } catch (error) {
      return "Error fetching location details:", error;
    }
  };

  const filteredListings = useMemo(() => {
    if (!listings) return [];

    return listings.filter((listing) => {
      if (!matchesBoatTypeFilter(listing, selectedOptions)) {
        return false;
      }

      if (selectedFiltersOptions.length > 0) {
        const hasMatchingFeature = selectedFiltersOptions.some((option) =>
          listing.features?.includes(option.value)
        );
        if (!hasMatchingFeature) return false;
      }

      const totalPassengers = appliedAdultsCount + appliedChildrenCount;
      if (totalPassengers > 0) {
        const boatCapacity = parseInt(listing.capacity) || 0;
        if (boatCapacity < totalPassengers) return false;
      }

      const selectedStart = dateRange[0];
      const selectedEnd = dateRange[1];
      if (selectedStart && selectedEnd) {
        if (isDateRangeOverlap(listing.listingEvents, selectedStart, selectedEnd)) {
          return false;
        }
      }

      if (searchAddress) {
        return matchesLocationFilter(listing, searchedLocationGeoData);
      }

      return true;
    });
  }, [
    listings,
    selectedOptions,
    selectedFiltersOptions,
    appliedAdultsCount,
    appliedChildrenCount,
    dateRange,
    searchAddress,
    searchedLocationGeoData,
  ]);

  // Get current listings
  const indexOfLastListing = currentPage * listingsPerPage;
  const indexOfFirstListing = indexOfLastListing - listingsPerPage;
  const currentListings = filteredListings?.slice(
    indexOfFirstListing,
    indexOfLastListing
  );

  // Calculate average rating from reviews
  const calculateAverageRating = (reviews) => {
    if (!reviews || reviews.length === 0) {
      return 0;
    }

    let totalRating = 0;
    let ratingCount = 0;

    reviews.forEach(review => {
      // Sum up all the rating categories
      const categoryRatings = [
        review["Listing Accuracy"],
        review["Itinerary & Experience"],
        review["Vessel & Equipment"],
        review["Departure & Return"],
        review["Value"],
        review["Communication"]
      ];

      // Filter out undefined values and calculate the average for this review
      const validRatings = categoryRatings.filter(rating => rating !== undefined && rating !== null);
      if (validRatings.length > 0) {
        const reviewAverage = validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length;
        totalRating += reviewAverage;
        ratingCount++;
      }
    });

    return ratingCount > 0 ? totalRating / ratingCount : 0;
  };

  // Derive map markers from filtered results so the map stays in sync
  const filteredBoatLocations = useMemo(() => {
    if (!filteredListings || filteredListings.length === 0) return [];

    return filteredListings
      .map(listing => {
        // Robustly find coordinates - use listingAddress first, fall back to storageAddress
        const lat = listing?.listingAddress?.lat ?? listing?.storageAddress?.lat;
        const lng = listing?.listingAddress?.lng ?? listing?.storageAddress?.lng;

        return { ...listing, _lat: lat, _lng: lng };
      })
      .filter((l) => hasValidCoordinates(l._lat, l._lng))
      .map((listing) => ({
        name: listing?.listingTitle,
        lat: parseFloat(listing._lat),
        lng: parseFloat(listing._lng),
        link: "listing/" + listing?.id,
        boatType: listing?.boatType,
        icon: `${process.env.REACT_APP_BASE_URL}/static/media/loading.33ed2d822776ba990a07.png`,
        listing: listing,
        ownerDetails: ownerDetails[listing?.id],
        locationName: locationNames[listing?.id],
        rating: calculateAverageRating(listingReviews[listing?.id] || []),
        reviewCount: listingReviews[listing?.id]?.length || 0,
      }));
  }, [filteredListings, ownerDetails, locationNames, listingReviews]);

  // Change page
  const nextPage = () => {
    setCurrentPage(currentPage + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevPage = () => {
    setCurrentPage(currentPage - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const containerStyle = {
    minHeight: "350px",
    borderRadius: "12px 12px 0 0",
    padding: "3px",
    backgroundSize: "cover",
    margin: "0px",
    backgroundPosition: "center",
  };

  const getLocationName = async (lat, lng) => {
    try {
      // Check if lat and lng are valid numbers
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
        console.warn("Invalid coordinates provided to getLocationName:", { lat, lng });
        return "Location not available";
      }

      const dropOffResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
      );

      const dropOffdata = await dropOffResponse.json();

      // Handle ZERO_RESULTS status
      if (dropOffdata.status === "ZERO_RESULTS") {
        console.warn("No results found for coordinates:", { lat, lng });
        return "Location not available";
      }

      const dropoffName =
        dropOffdata.results[0]?.formatted_address || "Location not found";
      return dropoffName;
    } catch (error) {
      console.error("Error fetching location name:", error);
      return "Location not available";
    }
  };

  const handleSearchInputChange = (e) => {
    // Handle case when called from button click without event
    if (e && e.target) {
      setSearchAddress(e.target.value);
    }
  };

  // Handle location search - geocode the address and set searchedLocation
  const handleLocationSearch = async () => {
    if (!grabSearchLocation || grabSearchLocation.trim() === "") {
      // Clear location filter if no location is entered
      setSearchedLocation(null);
      setSearchedLocationGeoData(null);
      setSearchAddress("");
      return;
    }

    try {
      // Geocode the address to get lat/lng  
      const geoData = await geocodeByAddress(grabSearchLocation);
      const latLng = await getLatLng(geoData[0]);

      // Set the searched location for filtering
      setSearchedLocation(latLng);
      setSearchedLocationGeoData(geoData);
      setSearchAddress(grabSearchLocation);

      console.log("Location geocoded:", latLng, geoData);
    } catch (error) {
      console.error("Error geocoding location:", error);
      // Clear location filter on error
      setSearchedLocation(null);
      setSearchedLocationGeoData(null);
    }
  };

  const filterBoatsForSelectedOptions = () => {
    setSelectedBoatTypeOptions(selectedOptions);
  };

  const filterBoatsForSelectedFilters = () => {
    setSelectedFiltersOptions(grabSelectedFilterOptions);
  };


  useEffect(() => {
    const preselectedBoatType = location.state?.preselectedBoatType;
    if (!preselectedBoatType || boatTypeOptions.length === 0) {
      return;
    }

    const preselectedOption = boatTypeOptions.find(
      (option) => option.value === preselectedBoatType
    );

    if (preselectedOption) {
      setSelectedBoatTypeOptions([preselectedOption]);
      setSelectedOptions([preselectedOption]);
    }

    // Consume the one-time preselect so Back from a listing does not overwrite filters
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, boatTypeOptions]);

  const [resetComplete, setResetComplete] = useState(false);

  const handleFiltersReset = () => {
    setSelectedOptions([]);
    setSelectedBoatTypeOptions([]);
    setSelectedFiltersOptions([]);
    setGrabSearchLocation("");
    setGrabSelectedFilterOptions([]);
    setSelectedDateRange([null, null]);
    setDateRange([null, null]);
    setAdultsCount(0);
    setChildrenCount(0);
    setAppliedAdultsCount(0);
    setAppliedChildrenCount(0);

    // Also clear the applied filter state so filteredListings stops filtering
    setSearchAddress("");
    setSearchedLocation(null);
    setSearchedLocationGeoData(null);

    setResetComplete(true); // Trigger effect once reset is complete
  };

  useEffect(() => {
    if (resetComplete) {
      setCurrentPage(1);
      setResetComplete(false);
    }
  }, [resetComplete]);

  useEffect(() => {
    if (selectedOptions?.length > 0) {
      // Extract the values (boat types) from the 'e' array
      const selectedBoatTypes = selectedOptions?.map((option) => option.value);

      // Filter the listings based on the selected boat types
      const filteredListings = listings.filter((listing) => {
        // Special handling for Fishing Guide/Charters
        if (selectedBoatTypes.includes("Fishing Guide") && listing.boatType === "Fishing Charter") {
          return true;
        }
        if (selectedBoatTypes.includes("Fishing Charter") && listing.boatType === "Fishing Guide") {
          return true;
        }
        // Standard filtering for other boat types
        return selectedBoatTypes.includes(listing.boatType);
      });
      setAvailableBoatsByBoatType(filteredListings?.length || 0);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (grabSelectedFilterOptions?.length > 0) {
      const filterValues = grabSelectedFilterOptions.map(
        (feature) => feature.value
      );

      const filteredByFeature = listings.filter((boat) =>
        boat.features.some((feature) => filterValues.includes(feature))
      );

      setAvailableBoatsByBoatFeatures(filteredByFeature?.length || 0);
    }
  }, [grabSelectedFilterOptions]);

  // Calculate available boats by passenger capacity
  useEffect(() => {
    const totalPassengers = adultsCount + childrenCount;
    if (totalPassengers > 0) {
      const filteredByCapacity = listings.filter((boat) => {
        const boatCapacity = parseInt(boat.capacity) || 0;
        return boatCapacity >= totalPassengers;
      });
      setAvailableBoatsByCapacity(filteredByCapacity?.length || 0);
    } else {
      setAvailableBoatsByCapacity(listings?.length || 0);
    }
  }, [adultsCount, childrenCount, listings]);

  const handleSelectAllBoatTypes = (e) => {
    if (e.target.checked) {
      setSelectedOptions(boatTypeOptions);
    } else {
      setSelectedOptions([]);
    }
  };

  const handleFeaturesSelectAll = (e) => {
    if (e.target.checked) {
      setGrabSelectedFilterOptions(filtersOptions);
    } else {
      setGrabSelectedFilterOptions([]);
    }
  };

  const handleIWannaFish = () => {
    // Find Fishing Charter and Fishing Guide options
    const fishingOptions = boatTypeOptions.filter(
      (option) =>
        option.value === "Fishing Charter" || option.value === "Fishing Guide"
    );

    if (fishingOptions.length > 0) {
      setSelectedOptions(fishingOptions);
      setSelectedBoatTypeOptions(fishingOptions);
      setCurrentPage(1);
      setShowFishModal(true);
    }
  };

  const handleBassBoat = () => {
    // Find Bass Boat option
    const bassBoatOptions = boatTypeOptions.filter(
      (option) => option.value === "Bass Boat"
    );

    if (bassBoatOptions.length > 0) {
      setSelectedOptions(bassBoatOptions);
      setSelectedBoatTypeOptions(bassBoatOptions);
      setCurrentPage(1);
      setShowBassBoatModal(true);
    }
  };

  return (
    <>
      {/* Home Page Action Buttons - centered above search bar */}
      <section className="cs-bg-dark pt-3 pb-0">
        <div className="container">
          {/* Row 1: General action buttons */}
          <div className="home-action-buttons d-flex justify-content-center flex-wrap gap-1">
            <Button
              as="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.target.blur();
                const searchBar = document.querySelector('.search-bar-container');
                if (searchBar) {
                  // Clear any existing timeout
                  if (window.glowAnimationTimeout) {
                    clearTimeout(window.glowAnimationTimeout);
                  }
                  searchBar.classList.remove('glow-effect');
                  // Force reflow to restart animation
                  void searchBar.offsetWidth;
                  searchBar.classList.add('glow-effect');
                  // Remove class after animation completes
                  window.glowAnimationTimeout = setTimeout(() => {
                    searchBar.classList.remove('glow-effect');
                  }, 3000);
                }
              }}
              className='btn btn-light cs-bg-orange text-cs-dark p-3 m-1'
            >
              <FontAwesomeIcon icon={faShip} className='icon me-2' /> Here to Rent
            </Button>
            <Button href="/dashboard" className='btn btn-light cs-bg-orange text-cs-dark p-3 m-1'>
              <FontAwesomeIcon icon={faAnchor} className='icon me-2' /> List my Boat
            </Button>
            <Button href="/dashboard" className='btn btn-light cs-bg-orange text-cs-dark p-3 m-1'>
              <FontAwesomeIcon icon={faGauge} className='icon me-2' /> Go to Dashboard
            </Button>
          </div>
          {/* Row 2: Fishing filter buttons */}
          <div className="home-action-buttons fishing-buttons d-flex justify-content-center flex-wrap gap-1 mb-1">
            <Button
              href=""
              onClick={(e) => {
                e.preventDefault();
                handleBassBoat();
              }}
              className='btn p-3 m-1'
              style={{ fontFamily: 'Fjalla One, sans-serif', backgroundColor: '#ffffff', color: '#000000' }}
            >
              <GiBoatFishing className='icon me-2' style={{ fontSize: '1.4em', strokeWidth: '1px', stroke: 'currentColor' }} /> Bass Boat
            </Button>
            <Button
              href=""
              onClick={(e) => {
                e.preventDefault();
                handleIWannaFish();
              }}
              className='btn p-3 m-1'
              style={{ fontFamily: 'Fjalla One, sans-serif', backgroundColor: '#78d5e9', color: '#000000' }}
            >
              <GiFishing className='icon me-2' style={{ fontSize: '1.4em', strokeWidth: '1px', stroke: 'currentColor' }} /> Fishing Charter
            </Button>
          </div>
        </div>
      </section>

      <section className="cs-bg-dark py-4 cs-section">
        <div className="container">
          {/* New Horizontal Search Bar */}
          <div className="search-bar-container">
            {/* Location Search */}
            <div className="search-field-wrapper">
              <CiGlobe className="search-icon" />
              <div style={{ flex: 1 }}>
                <PlacesAutocomplete
                  value={grabSearchLocation}
                  onChange={(newAddress) => {
                    setGrabSearchLocation(newAddress);
                  }}
                  className="w-100"
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <div className="position-relative">
                      <input
                        {...getInputProps({
                          // placeholder removed in favor of overlay
                          className: "form-control bg-transparent border-0 p-0",
                          name: "location",
                          style: { boxShadow: 'none' }
                        })}
                      />
                      {!grabSearchLocation && (
                        <span className="custom-placeholder-overlay">
                          Location
                        </span>
                      )}
                      <div className="autocomplete-dropdown-container position-absolute location-dropdown-container bg-white shadow rounded mt-1" style={{ zIndex: 1000, top: '100%', left: 0 }}>
                        {loading && <div className="p-2">Loading...</div>}
                        {suggestions.map((suggestion, index) => {
                          const className = suggestion.active
                            ? "suggestion-item--active bg-light p-2 cursor-pointer"
                            : "suggestion-item p-2 cursor-pointer";
                          return (
                            <div
                              key={index}
                              {...getSuggestionItemProps(suggestion, {
                                className,
                              })}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </PlacesAutocomplete>
              </div>
            </div>

            {/* Date Search */}
            <DateRangeSelect
              dateRange={selectedDateRange}
              onChange={handleDateRangeChange}
              placeholder="Date"
            />

            {/* Boat Type Selection */}
            <BoatTypeSelect
              options={boatTypeOptions}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              placeholder="Type"
            />
            {/* Passengers Selection */}
            <PassengerSelect
              adultsCount={adultsCount}
              setAdultsCount={setAdultsCount}
              childrenCount={childrenCount}
              setChildrenCount={setChildrenCount}
            />

            {/* Find Your Boat Button */}
            <div className="search-button-wrapper d-flex flex-column align-items-center">
              <Button
                className="search-button"
                onClick={async () => {
                  await handleLocationSearch();
                  filterBoatsForSelectedOptions();
                  filterBoatsForSelectedFilters();
                  handleFilterByDateRange();
                  setAppliedAdultsCount(adultsCount);
                  setAppliedChildrenCount(childrenCount);
                  setCurrentPage(1);
                }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} className='icon me-2' />Search
              </Button>

            </div>
          </div>

          {/* Search Action Links - Moved outside search bar */}
          <div className="d-flex justify-content-end mt-2 mb-1">
            <div className="search-action-links">
              <span style={{ fontSize: '11px', color: '#fff' }}>
                ({filteredListings?.length || 0}) results
              </span>
              <span className="action-link-divider text-white">|</span>
              <button
                className="map-toggle-link text-white"
                onClick={() => setIsMapVisible(!isMapVisible)}
                type="button"
              >
                {isMapVisible ? 'Close Map' : 'Show Map'}
              </button>
              <span className="action-link-divider text-white">|</span>
              <Button
                className="reset-button text-white"
                onClick={() => {
                  handleFiltersReset();
                }}
              >
                Reset filters
              </Button>
              <span className="action-link-divider text-white d-none">|</span>
              <Button
                className="debug-button text-white d-none"
                onClick={() => setShowPerformanceScores(!showPerformanceScores)}
              >
                {showPerformanceScores ? 'Hide Scores' : 'Show Scores'}
              </Button>
            </div>
          </div>

          {/* Map - Full Width matching search bar (Collapsible) */}
          {/* Map - Full Width matching search bar (Collapsible) */}
          {isMapVisible && (
            <>
              {isBoatsLoading ? (
                <div className="p-0 mb-4" style={{ height: "400px" }}>
                  <Skeleton
                    sx={{
                      bgcolor: "grey.700",
                      width: "100%",
                      height: "100%",
                      borderRadius: "0.25rem"
                    }}
                    variant="rectangular"
                  />
                </div>
              ) : (
                <div className="p-0 mb-4" style={{ height: "400px" }}>
                  <div className="rounded overflow-hidden h-100 bg-light">
                    <GoogleMaps boatLocations={filteredBoatLocations || []} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Boat Listings */}
          <Row className="boatListings mt-3">
            {isBoatsLoading ? (
              <>
                {[...Array(9)].map((_, i) => (
                  <Col md={4} key={i} className="mb-4 px-3">
                    <Skeleton
                      sx={{
                        bgcolor: "grey.700",
                        width: "100%",
                        height: "380px",
                        borderRadius: "12px"
                      }}
                      variant="rectangular"
                    />
                  </Col>
                ))}
              </>
            ) : (
              <>
                {currentListings?.length === 0 && (
                  <Col xs={12} className="text-center text-white py-5">
                    No boats match your filters. Try adjusting By Type, Features, Location, or Date.
                  </Col>
                )}
                {currentListings?.map((listing) => (
                  <Col xs={12} md={4} className="mb-4" key={listing.id}>
                    {/* Mobile Card Layout - Horizontal (visible on small screens) */}
                    <Link
                      to={`/listing/${listing.id}`}
                      className="mobile-listing-card"
                    >
                      {/* Left: Image */}
                      <div className="mobile-card-image-container">
                        <img
                          src={listing.featured_image}
                          alt={listing.listingTitle}
                          className="mobile-card-image"
                        />
                        {/* Owner overlay on image */}
                        <div
                          className="mobile-card-owner-overlay"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => goToOwnerBoats(event, listing.ownerId)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              goToOwnerBoats(event, listing.ownerId);
                            }
                          }}
                        >
                          <div className="mobile-card-owner-info">
                            <UserAvatar
                              src={ownerDetails[listing.id]?.avatar}
                              name={`${ownerDetails[listing.id]?.firstName || ""} ${ownerDetails[listing.id]?.lastName || ""}`}
                              size={28}
                            />
                            <span className="mobile-card-owner-name">
                              {ownerDetails[listing.id] ? `${ownerDetails[listing.id].firstName} ${ownerDetails[listing.id].lastName}` : "Loading..."}
                            </span>
                          </div>
                          <div className="mobile-card-rating">
                            <div className="star-icons">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  size={8}
                                  style={{
                                    color: star <= (calculateAverageRating(listingReviews[listing.id] || []) || 0) ? "#FFD700" : "#e4e5e9"
                                  }}
                                />
                              ))}
                            </div>
                            <span className="rating-text">
                              {listingReviews[listing.id] ?
                                `${calculateAverageRating(listingReviews[listing.id]).toFixed(1)} (${listingReviews[listing.id].length})` :
                                "No reviews"}
                            </span>
                          </div>
                        </div>
                        {/* Boat specs widget overlay on bottom-left of image - only show when boat length is available */}
                        {listing.boatLength && (
                          <div className="mobile-card-boat-specs">
                            <RiShip2Fill className="boat-specs-icon" />
                            <span className="boat-specs-length">{listing.boatLength} ft</span>
                          </div>
                        )}
                      </div>

                      {/* Right: Details */}
                      <div className="mobile-card-details">
                        {/* Title with instant badge */}
                        <div className="mobile-card-title-row">
                          <h4 className="mobile-card-title">{listing.listingTitle}</h4>
                          {listing.fishingGuideDetails?.calendarRules?.instantBook && (
                            <span className="mobile-card-instant-badge">
                              <i className="material-icons" style={{ fontSize: "10px" }}>flash_on</i>
                              Instant
                            </span>
                          )}
                        </div>

                        {/* Location */}
                        <p className="mobile-card-location">
                          {locationNames[listing.id] || "Loading..."}
                        </p>

                        {/* Boat type info */}
                        <div className="mobile-card-boat-info">
                          {listing.boatType && (
                            <>
                              <FontAwesomeIcon icon={faShip} className="boat-icon" />
                              <span>{listing.boatType}</span>
                              {listing.capacity && <span>• Up to {listing.capacity} people</span>}
                            </>
                          )}
                          {/* Performance Score Debug Display */}
                          {showPerformanceScores && (
                            <div style={{
                              backgroundColor: '#007bff',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              marginLeft: '5px',
                              display: 'inline-block'
                            }}>
                              Score: {listing.performanceScore?.toFixed(2) || '0.00'}
                            </div>
                          )}
                        </div>

                        {/* Features - same as desktop version */}
                        {listing.features && listing.features.length > 0 && (
                          <div className="mobile-card-features">
                            {(listing.priorityFeatures && listing.priorityFeatures.length > 0 ? listing.priorityFeatures : listing.features).slice(0, 3).map((feature, idx) => (
                              <span key={idx} className="mobile-card-feature">
                                ✓ {feature}
                              </span>
                            ))}
                            {listing.features.length > 3 && (
                              <span style={{ fontSize: "10px", color: "#666" }}>
                                +{listing.features.length - 3} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* License Information for Fishing Charter/Guide - same as desktop */}
                        {(listing.boatType === "Fishing Charter" || listing.boatType === "Fishing Guide") &&
                          listing.fishingGuideDetails?.legal?.licenses && (
                            <div className="mobile-card-licenses">
                              {listing.fishingGuideDetails.legal.licenses.sixPackLicense && (
                                <span className="mobile-card-license">✓ 6-Pack</span>
                              )}
                              {listing.fishingGuideDetails.legal.licenses.masterCaptainLicense && (
                                <span className="mobile-card-license">✓ Master Captain</span>
                              )}
                              {listing.fishingGuideDetails.legal.licenses.coastGuardCOI && (
                                <span className="mobile-card-license">✓ COI Vessel</span>
                              )}
                              {listing.fishingGuideDetails.legal.licenses.fishingCharterInsurance && (
                                <span className="mobile-card-license">✓ Insured</span>
                              )}
                              {listing.fishingGuideDetails.legal.licenses.stateLicensed9Miles && (
                                <span className="mobile-card-license">✓ State Licensed</span>
                              )}
                              {listing.fishingGuideDetails.legal.licenses.federallyLicensedOver9Miles && (
                                <span className="mobile-card-license">✓ Federally Licensed</span>
                              )}
                            </div>
                          )}

                        {/* Price section test */}
                        <div className="mobile-card-price-section">
                          <div>
                            <span className="mobile-card-price-label">Trip starts from</span>
                            <div className="mobile-card-price">
                              US ${listing.pricing?.tripsStartingFrom || listing.pricing?.hourlyRate || listing.pricing?.hourlyOptions?.[0]?.price || "N/A"}
                            </div>
                          </div>
                          <button className="mobile-card-cta-btn">
                            See availability →
                          </button>
                        </div>
                      </div>
                    </Link>

                    {/* Desktop Card Layout - Vertical (visible on md and up) */}
                    <Link
                      to={`/listing/${listing.id}`}
                      className="desktop-listing-card"
                      style={{ textDecoration: "none", height: "100%" }}
                    >
                      <div className="position-relative">
                        {/* Owner info above the image */}
                        <div
                          className="owner-info-overlay"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => goToOwnerBoats(event, listing.ownerId)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              goToOwnerBoats(event, listing.ownerId);
                            }
                          }}
                          style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            zIndex: 10,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "rgba(26, 26, 26, 0.1)",
                            padding: "5px 10px",
                            borderRadius: "10px",
                            maxWidth: "75%"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div className="owner-avatar-wrapper" style={{ position: "relative" }}>
                              <UserAvatar
                                src={ownerDetails[listing.id]?.avatar}
                                name={`${ownerDetails[listing.id]?.firstName || ""} ${ownerDetails[listing.id]?.lastName || ""}`}
                                size={50}
                                style={{ marginRight: "8px" }}
                              />
                              {ownerDetails[listing.id]?.role === "Pro Staff" && (
                                <span
                                  style={{
                                    position: "absolute",
                                    bottom: "-3px",
                                    right: "3px",
                                    backgroundColor: "#ffc107",
                                    color: "#000",
                                    fontSize: "7px",
                                    padding: "1px 3px",
                                    borderRadius: "3px",
                                    fontWeight: "bold"
                                  }}
                                >
                                  PRO
                                </span>
                              )}
                            </div>
                            <div className="owner-name" style={{ color: "white", fontSize: "12px", fontWeight: "500" }}>
                              {ownerDetails[listing.id] ? `${ownerDetails[listing.id].firstName} ${ownerDetails[listing.id].lastName}` : "Loading..."}
                            </div>
                          </div>

                          {/* Rating below owner name */}
                          <div className="owner-rating-row" style={{ display: "flex", alignItems: "center", marginTop: "4px", marginLeft: "2px" }}>
                            <div className="star-icons" style={{ display: "flex", gap: "2px" }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  size={10}
                                  style={{
                                    color: star <= (calculateAverageRating(listingReviews[listing.id] || []) || 0) ? "#FFD700" : "#e4e5e9"
                                  }}
                                />
                              ))}
                            </div>
                            <span className="rating-text" style={{ fontSize: "10px", color: "white", marginLeft: "3px" }}>
                              {listingReviews[listing.id] ?
                                `${calculateAverageRating(listingReviews[listing.id]).toFixed(1)} (${listingReviews[listing.id].length})` :
                                "No reviews"}
                            </span>
                          </div>
                        </div>

                        <div
                          className="row productCard p-3 position-relative"
                          style={{
                            ...containerStyle,
                            backgroundImage: `url(${listing.featured_image})`,
                            minHeight: "280px", /* Reduced height from 350px to 280px */
                          }}
                        >
                          <div
                            className="overlay"
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              content: '""',
                              backgroundColor: "rgba(0, 0, 0, 0.22)",
                            }}
                          ></div>
                          <div
                            className="col-12"
                            style={{
                              justifyContent: "space-between",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <p></p>
                            <div className="row" style={{ zIndex: "9" }}>
                              <div className="col-12"></div>
                              <div className="row">
                                {/* Boat name removed from image overlay */}
                              </div>
                            </div>
                          </div>
                          {/* Boat length specs widget - bottom left of image */}
                          {listing.boatLength && (
                            <div className="desktop-card-boat-specs">
                              <RiShip2Fill className="boat-specs-icon" />
                              <span className="boat-specs-length">{listing.boatLength} ft</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Details Section */}
                      <div
                        style={{
                          backgroundColor: "white",
                          padding: "16px",
                          borderRadius: "0 0 12px 12px",
                          marginTop: "-3px",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column"
                        }}
                      >
                        {/* Business Name and Address */}
                        <div style={{ marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: 0 }}>
                              {listing.listingTitle}
                            </h4>
                            {listing.fishingGuideDetails?.calendarRules?.instantBook && (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                backgroundColor: "rgba(221, 180, 103, 0.15)",
                                color: "#ddb467",
                                padding: "4px 10px",
                                borderRadius: "14px",
                                border: "1.5px solid #ddb467",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                fontFamily: '"Fjalla One", sans-serif',
                                letterSpacing: "0.3px"
                              }}>
                                <i className="material-icons" style={{ fontSize: "14px", color: "#ddb467" }}>flash_on</i>
                                Instant
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}>
                            {locationNames[listing.id] || "Loading..."}
                          </p>
                        </div>

                        <hr style={{ margin: "12px 0", borderColor: "#e0e0e0" }} />

                        {/* Boat Type and Capacity */}
                        <div style={{ marginBottom: "8px" }}>
                          <span style={{ color: "#666", fontSize: "14px" }}>
                            {listing.boatType && (
                              <>
                                <FontAwesomeIcon icon={faShip} style={{ marginRight: "6px", color: "#ff6b35" }} />
                                {listing.boatType}
                              </>
                            )}
                            {listing.capacity && (
                              <>
                                {listing.boatType && " • "}
                                Up to {listing.capacity} people {listing.boatLength && ` -  ${listing.boatLength}ft`}
                              </>
                            )}

                            {/* Performance Score Debug Display */}
                            {showPerformanceScores && (
                              <div style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                marginLeft: '10px',
                                display: 'inline-block'
                              }}>
                                Score: {listing.performanceScore?.toFixed(2) || '0.00'}
                              </div>
                            )}
                          </span>
                        </div>

                        {/* Features */}
                        <div style={{ marginBottom: "12px", minHeight: "24px" }}>
                          {listing.features && listing.features.length > 0 ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              {(listing.priorityFeatures && listing.priorityFeatures.length > 0 ? listing.priorityFeatures : listing.features).slice(0, 3).map((feature, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: "13px",
                                    color: "#28a745",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  ✓ {feature}
                                </span>
                              ))}
                              {listing.features.length > 3 && (
                                <span style={{ fontSize: "13px", color: "#666" }}>
                                  +{listing.features.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <div style={{ height: "19px" }}></div>
                          )}
                        </div>

                        {/* License Information for Fishing Charter */}
                        {(listing.boatType === "Fishing Charter" || listing.boatType === "Fishing Guide") &&
                          listing.fishingGuideDetails?.legal?.licenses && (
                            <div style={{ marginBottom: "12px" }}>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
                                {listing.fishingGuideDetails.legal.licenses.sixPackLicense && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> 6-Pack
                                  </span>
                                )}
                                {listing.fishingGuideDetails.legal.licenses.masterCaptainLicense && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> Master Captain
                                  </span>
                                )}
                                {listing.fishingGuideDetails.legal.licenses.coastGuardCOI && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> COI Vessel
                                  </span>
                                )}
                                {listing.fishingGuideDetails.legal.licenses.fishingCharterInsurance && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> Insured
                                  </span>
                                )}
                                {listing.fishingGuideDetails.legal.licenses.stateLicensed9Miles && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> State Licensed
                                  </span>
                                )}
                                {listing.fishingGuideDetails.legal.licenses.federallyLicensedOver9Miles && (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#28a745",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span style={{ marginRight: "4px", fontSize: "16px", color: "#28a745" }}>✓</span> Federally Licensed
                                  </span>
                                )}
                              </div>
                            </div>
                          )}


                        {/* Pricing */}
                        {listing.pricing && (
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "12px",
                            marginTop: "auto"
                          }}>
                            <div>
                              <span style={{ fontSize: "14px", color: "#666" }}>Trip starts from</span>
                              <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>
                                US ${listing.pricing.tripsStartingFrom || listing.pricing.hourlyRate || listing.pricing.hourlyOptions?.[0]?.price || "N/A"}
                              </div>
                            </div>
                            <Button
                              style={{
                                backgroundColor: "#ddb467",
                                border: "none",
                                color: "#000",
                                padding: "8px 16px",
                                fontSize: "14px",
                                fontWeight: "600",
                                fontFamily: '"Fjalla One", sans-serif'
                              }}
                            >
                              See availability →
                            </Button>
                          </div>
                        )}
                      </div>
                    </Link>
                  </Col>
                ))}
              </>
            )}
          </Row>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={prevPage}
              className={`btn ${currentPage === 1 ? "btn-light" : "btn-light"
                } m-1`}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              onClick={nextPage}
              className={`btn ${currentListings.length < listingsPerPage
                ? "btn-light"
                : "btn-light"
                } m-1`}
              disabled={currentListings.length < listingsPerPage}
            >
              Next
            </button>
          </div>
        </div>
      </section >

      {/* Fishing Charter Modal */}
      <Modal
        show={showFishModal}
        onHide={() => setShowFishModal(false)}
        centered
        className="fishing-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="font-fjalla text-cs-dark" style={{ display: 'flex', alignItems: 'center' }}>
            <GiBoatFishing className="me-2" style={{ fontSize: '2em', color: '#87CEEB' }} />
            Fishing Charter Selected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3 pb-4">
          <p className="mb-3" style={{ fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
            Great choice! Now use the search bar to select your:
          </p>
          <ul className="mb-0" style={{ fontSize: '14px', color: '#555', paddingLeft: '20px' }}>
            <li className="mb-2"><strong>Location</strong> – Where do you want to fish?</li>
            <li className="mb-2"><strong>Date</strong> – When are you available?</li>
            <li className="mb-0"><strong>Passengers</strong> – How many are joining?</li>
          </ul>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            style={{
              backgroundColor: '#87CEEB',
              border: 'none',
              color: '#1a1a1a',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: '"Fjalla One", sans-serif',
              borderRadius: '4px'
            }}
            onClick={() => {
              setShowFishModal(false);
              // Small delay to let the modal close before scrolling or showing glow
              setTimeout(() => {
                const searchBar = document.querySelector('.search-bar-container');
                if (searchBar) {
                  searchBar.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  searchBar.classList.add('glow-effect');
                  setTimeout(() => {
                    searchBar.classList.remove('glow-effect');
                  }, 3000);
                }
              }, 300);
            }}
          >
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bass Boat Modal */}
      <Modal
        show={showBassBoatModal}
        onHide={() => setShowBassBoatModal(false)}
        centered
        className="fishing-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="font-fjalla text-cs-dark" style={{ display: 'flex', alignItems: 'center' }}>
            <GiBoatFishing className="text-cs-orange me-2" style={{ fontSize: '2em' }} />
            Bass Boat Selected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3 pb-4">
          <p className="mb-3" style={{ fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
            Great choice! Now use the search bar to select your:
          </p>
          <ul className="mb-0" style={{ fontSize: '14px', color: '#555', paddingLeft: '20px' }}>
            <li className="mb-2"><strong>Location</strong> – Where do you want to fish?</li>
            <li className="mb-2"><strong>Date</strong> – When are you available?</li>
            <li className="mb-0"><strong>Passengers</strong> – How many are joining?</li>
          </ul>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            style={{
              backgroundColor: '#ddb467',
              border: 'none',
              color: '#1a1a1a',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: '"Fjalla One", sans-serif',
              borderRadius: '4px'
            }}
            onClick={() => {
              setShowBassBoatModal(false);
              // Small delay to let the modal close before scrolling or showing glow
              setTimeout(() => {
                const searchBar = document.querySelector('.search-bar-container');
                if (searchBar) {
                  searchBar.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  searchBar.classList.add('glow-effect');
                  setTimeout(() => {
                    searchBar.classList.remove('glow-effect');
                  }, 3000);
                }
              }, 300);
            }}
          >
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
